import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { emitSendStatus } from "./SendStatusOverlay";
import { getCachedThumb, fetchThumb } from "../lib/thumbCache";
import { modSelect, noShiftTextSelect } from "../lib/mouseSelect";
import { pdfFirstPageImage } from "../lib/pdfThumb";
import { LETTER_CSS, sanitizeEmailHtml, applyJustifyTypography, letterHtmlToEmail, emailHeaderHtml } from "../lib/letterPdf";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Mail, Loader2, Send, Paperclip, AlertTriangle, List, LayoutGrid,
  FileText, File as FileIcon, Image as ImageIcon, Eye, Folder, Home, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import * as pdfjsLib from "pdfjs-dist";
import { RecipientField, RubricaDialog } from "./ContactPicker";
import SuggeritiDestinatariDialog from "./SuggeritiDestinatariDialog";
import TestiRapidiPicker from "./TestiRapidiPicker";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import EmailPreviewModal from "./EmailPreviewModal";
import { buildPraticaOggetto } from "../lib/oggetto";
pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const isImg = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => (ct || "") === "application/pdf";
// Cede un frame al browser così React dipinge lo stato di avanzamento PRIMA del lavoro pesante.
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));

function typeIcon(ct, cls) {
  if (isImg(ct)) return <ImageIcon className={cls} />;
  if (isPdf(ct)) return <FileText className={cls} />;
  return <FileIcon className={cls} />;
}

async function renderPdfThumb(blob) {
  try {
    const buf = await blob.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    const v = page.getViewport({ scale: 300 / vp.width });
    const canvas = document.createElement("canvas");
    canvas.width = v.width; canvas.height = v.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: v }).promise;
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch { return null; }
}

async function renderPdfThumbFromUrl(url) {
  try {
    const pdf = await pdfjsLib.getDocument(url).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    const v = page.getViewport({ scale: 300 / vp.width });
    const canvas = document.createElement("canvas");
    canvas.width = v.width; canvas.height = v.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: v }).promise;
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch { return null; }
}

function AttFolderLine({ f, className = "" }) {
  return (
    <div className={`flex items-center gap-1 font-bold text-sky-600 truncate ${className}`} data-testid={`sle-att-folder-${f.id}`}>
      {f.folder_id ? <Folder className="h-3 w-3 shrink-0" /> : <Home className="h-3 w-3 shrink-0" />}
      <span className="truncate">{f.folder_id ? (f.folder_name || "Cartella") : "HOME"}</span>
    </div>
  );
}

function AttachmentThumb({ pid, file }) {
  const _isImg = isImg(file.content_type);
  const _isPdf = isPdf(file.content_type);
  const hasThumb = _isImg || _isPdf;
  const [src, setSrc] = useState(() => (hasThumb ? getCachedThumb(file.id) : null));
  const [failed, setFailed] = useState(false);
  const ct = file.content_type;

  useEffect(() => {
    let active = true;
    if (!hasThumb) return;
    (async () => {
      try {
        const url = await fetchThumb(pid, file.id, ct);
        if (!active) return;
        if (url) { setSrc(url); return; }
        if (_isPdf) {
          const p = await pdfFirstPageImage(`s-${file.id}`, async () => (await api.get(`/studio/pratiche/${pid}/allegati-files/${file.id}/content`, { responseType: "blob" })).data);
          if (!active) return;
          if (p) { setSrc(p); return; }
        }
        setFailed(true);
      } catch { if (active) setFailed(true); }
    })();
    return () => { active = false; };
  }, [pid, file.id, ct, hasThumb, _isPdf]);

  if (src) {
    return <img src={src} alt={file.nome} draggable={false} className="h-full w-full object-contain pointer-events-none" />;
  }
  if (hasThumb && !failed) {
    return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  }
  return typeIcon(ct, "h-8 w-8 text-slate-500");
}

export default function SendLetterEmailDialog({ open, onOpenChange, pratica, letterName, buildLetterPdfB64, assistitoName, clienteId, letterHtml, letterId, emailHead, onSent }) {
  const { user } = useAuth();
  const viewKey = `sle_att_view_${user?.id || "anon"}`;
  const [status, setStatus] = useState(null);
  const [pecStatus, setPecStatus] = useState(null);
  const [account, setAccount] = useState("email");
  const [data, setData] = useState({ contacts: [], attachments: [], signature: "", rubrica: [] });
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [ccn, setCcn] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [letterInserted, setLetterInserted] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [attView, setAttView] = useState(() => localStorage.getItem(viewKey) || "list");
  const [fotoOpen, setFotoOpen] = useState({});
  const [openSecs, setOpenSecs] = useState({});
  const sigKey = `sle_include_sig_${user?.id || "anon"}`;
  const [includeSig, setIncludeSig] = useState(() => localStorage.getItem(sigKey) !== "off");
  const changeIncludeSig = (v) => { setIncludeSig(v); localStorage.setItem(sigKey, v ? "on" : "off"); };
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState(null); // 'pdf' | 'upload' | 'server' | 'done'
  const [uploadPct, setUploadPct] = useState(0);
  const [rubricaField, setRubricaField] = useState(null); // 'to' | 'cc' | 'ccn'
  const [previewIndex, setPreviewIndex] = useState(-1);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [suggOpen, setSuggOpen] = useState(false);
  const bodyRef = useRef(null);
  const toFlushRef = useRef(null);
  const ccFlushRef = useRef(null);
  const ccnFlushRef = useRef(null);
  const flushRecipients = () => {
    const t = toFlushRef.current ? toFlushRef.current() : to;
    const c = ccFlushRef.current ? ccFlushRef.current() : cc;
    const n = ccnFlushRef.current ? ccnFlushRef.current() : ccn;
    return { t, c, n };
  };
  const insertQuickText = (t) => {
    const text = String(t || "");
    const el = bodyRef.current;
    if (el) {
      el.focus();
      const ok = document.execCommand && document.execCommand("insertText", false, text);
      if (!ok) el.innerHTML += text.replace(/\n/g, "<br>");
      setBody(el.innerHTML);
    } else {
      setBody((b) => (b ? `${b}${text}` : text));
    }
  };

  // Blocco lettera formattato (HTML) inserito nel corpo: reso non editabile per preservare il layout.
  // La larghezza è vincolata a quella della pagina A4 reale (624px) così il testo non risulta
  // troppo largo e le colonne (tabella) mantengono le proporzioni dell'anteprima/PDF.
  const buildLetterEmbed = () =>
    `<div class="sle-letter-embed pgp-pdfroot" contenteditable="false" style="max-width:624px;margin-left:auto;margin-right:auto"><div class="pgp-doc">${applyJustifyTypography(sanitizeEmailHtml(letterHtml || ""))}</div></div>`;

  const toggleLetter = () => {
    const el = bodyRef.current;
    if (!letterHtml || !el) return;
    if (!letterInserted) {
      const cur = el.innerHTML;
      const hasText = cur && cur.replace(/<br\s*\/?>(\s)*/gi, "").replace(/<[^>]+>/g, "").trim();
      el.innerHTML = `${cur}${hasText ? "<p><br></p>" : ""}${buildLetterEmbed()}`;
      setBody(el.innerHTML);
      setLetterInserted(true);
    } else {
      el.querySelectorAll(".sle-letter-embed").forEach((n) => n.remove());
      setBody(el.innerHTML);
      setLetterInserted(false);
    }
  };

  const curStatus = account === "pec" ? pecStatus : status;
  const curFrom = account === "pec"
    ? (pecStatus?.from || pecStatus?.mailbox || pecStatus?.from_email || "")
    : (status?.from_email || status?.from || status?.mailbox || "");
  const canSend = curStatus?.configured && curStatus?.enabled;
  const changeView = (v) => { setAttView(v); localStorage.setItem(viewKey, v); };

  const attSections = useMemo(() => {
    const out = [];
    const sch = pratica?.assistito_scheda || {};
    const pName = pratica?.nome_assistito || sch.ragione_sociale || [sch.cognome, sch.nome].filter(Boolean).join(" ") || "Cliente principale";
    (data.attachments || []).forEach((c) => {
      const key = c.sezione || "principale";
      let s = out.find((x) => x.key === key);
      if (!s) { s = { key, nome: key === "principale" ? pName : (c.sezione_nome || "Cliente"), cats: [] }; out.push(s); }
      s.cats.push(c);
    });
    return out.sort((a, b) => (a.key === "principale" ? -1 : b.key === "principale" ? 1 : 0));
  }, [data.attachments, pratica]);
  const flatFiles = useMemo(() => attSections.flatMap((s) => s.cats.flatMap((c) => c.files)), [attSections]);
  const selectedFiles = flatFiles.filter((f) => selectedIds[f.id]);
  const maxAttachBytes = data.max_attach_bytes || 20 * 1024 * 1024;
  const selectedTotal = selectedFiles.reduce((s, f) => s + (f.size || 0), 0);
  const overLimit = selectedTotal > maxAttachBytes;
  const letterPdfName = `${(subject || "lettera").replace(/[^\p{L}\p{N}\-_]+/gu, "_")}.pdf`;
  const previewAttachments = [{ nome: `${letterName || "Lettera corrente"} (PDF)`, size: null }, ...selectedFiles.map((f) => ({ nome: f.nome, size: f.size }))];

  const FIELD = { to: { value: to, set: setTo, label: "Destinatari" }, cc: { value: cc, set: setCc, label: "CC" }, ccn: { value: ccn, set: setCcn, label: "CCN" } };

  const phaseLabel = { pdf: "Generazione PDF della lettera…", upload: "Caricamento allegati…", server: "Invio in corso…", done: "Completato" }[phase] || "";
  const progressPct = phase === "pdf" ? 15
    : phase === "upload" ? Math.min(90, 15 + Math.round(uploadPct * 0.75))
    : phase === "server" ? 95
    : phase === "done" ? 100 : 0;
  useEffect(() => {
    if (sending && phase && phase !== "done") emitSendStatus({ state: "sending", label: phaseLabel, pct: progressPct });
  }, [sending, phase, phaseLabel, progressPct]);

  useEffect(() => {
    if (!open) return;
    setTo([]); setCc([]); setCcn([]); setSelectedIds({}); setRubricaField(null); setPreviewIndex(-1); setEmailPreviewOpen(false); setAccount("email"); setLetterInserted(false); setFotoOpen({}); setOpenSecs({});
    setSubject(buildPraticaOggetto(pratica) || letterName || pratica?.numero_pratica || "");
    setLoading(true);
    Promise.all([
      api.get("/studio/email/status").catch(() => ({ data: { configured: false, enabled: false } })),
      api.get("/studio/mail/pec/status").catch(() => ({ data: { configured: false, enabled: false } })),
      api.get(`/studio/pratiche/${pratica?.id}/email-data`).catch(() => ({ data: { contacts: [], attachments: [], signature: "", rubrica: [] } })),
    ]).then(([st, pst, ed]) => {
      setStatus(st.data);
      setPecStatus(pst.data);
      setData(ed.data);
      setBody("");
      if (localStorage.getItem(sigKey) === null) setIncludeSig(ed.data.firma_enabled !== false);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Apre automaticamente la finestra suggerimenti destinatari una sola volta all'apertura del dialog.
  useEffect(() => {
    if (open && pratica?.id) setSuggOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFromSuggeriti = (chosen, chosenMode) => {
    if (chosenMode === "pec" || chosenMode === "email") setAccount(chosenMode);
    mergeInto("to", chosen.map((c) => ({ name: c.name, email: c.address })));
  };

  const lastAtt = useRef(null);
  const toggleAtt = (id) => { lastAtt.current = id; setSelectedIds((s) => ({ ...s, [id]: !s[id] })); };
  const attClick = (id, ids) => (e) => modSelect(e, id, ids || flatFiles.map((x) => x.id), lastAtt,
    (x) => setSelectedIds((s) => ({ ...s, [x]: !s[x] })),
    (ids) => setSelectedIds((s) => ({ ...s, ...Object.fromEntries(ids.map((x) => [x, true])) })));

  const mergeInto = (field, list) => {
    const cur = FIELD[field].value;
    const seen = new Set(cur.map((c) => c.email.toLowerCase()));
    const add = list.filter((c) => c.email && !seen.has(c.email.toLowerCase()));
    if (add.length) FIELD[field].set([...cur, ...add]);
  };

  const saveContact = async (email) => {
    try {
      await api.post("/studio/rubrica/contatti", { email });
      setData((d) => {
        const cats = [...(d.rubrica || [])];
        const contact = { name: email, email, role: "Salvato" };
        const idx = cats.findIndex((c) => c.key === "salvati");
        if (idx >= 0) cats[idx] = { ...cats[idx], contacts: [...cats[idx].contacts, contact] };
        else cats.unshift({ key: "salvati", label: "Salvati in rubrica", contacts: [contact] });
        return { ...d, rubrica: cats };
      });
      toast.success("Contatto salvato in rubrica");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito");
    }
  };

  const send = async () => {
    const { t: finalTo, c: finalCc, n: finalCcn } = flushRecipients();
    const recipients = finalTo.map((t) => t.email);
    if (!recipients.length) { toast.error("Inserisci almeno un destinatario"); return; }
    if (!subject.trim()) { toast.error("Inserisci l'oggetto"); return; }
    if (overLimit) { toast.error(`Allegati troppo grandi (${fmtBytes(selectedTotal)}). Limite ${fmtBytes(maxAttachBytes)}.`); return; }
    setSending(true);
    setUploadPct(0);
    setPhase("pdf");
    try {
      await nextFrame(); // dipinge "Generazione PDF…" prima del lavoro bloccante di html2canvas
      const pdfB64 = await buildLetterPdfB64(); // usa la cache se la lettera è già stata generata
      const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
      // Corpo = HTML dell'editor. Il blocco lettera embeddato viene ri-generato con stili inline per i client email.
      const container = document.createElement("div");
      container.innerHTML = (bodyRef.current ? bodyRef.current.innerHTML : body) || "";
      container.querySelectorAll(".sle-letter-embed").forEach((n) => {
        const wrap = document.createElement("div");
        wrap.setAttribute("style", "margin-top:12px");
        wrap.innerHTML = letterHtmlToEmail(letterHtml || "");
        n.replaceWith(wrap);
      });
      const parts = [emailHeaderHtml(emailHead), container.innerHTML || ""];
      if (includeSig && data.signature) parts.push(`<div style="white-space:pre-wrap;margin-top:12px">${esc(`--\n${data.signature}`)}</div>`);
      const htmlBody = `<div style="font-family:Georgia,'Times New Roman',serif;font-size:15px;line-height:1.6;color:#1f2937">${parts.join("")}</div>`;
      const attachment_ids = Object.keys(selectedIds).filter((k) => selectedIds[k]);
      const num = pratica?.numero_pratica;
      const tag = num ? `[PRATICA-${num}]` : "";
      const subj = tag && !subject.includes(tag) ? `${subject.trim()} ${tag}`.trim() : subject.trim();
      const attachments = pdfB64
        ? [{ nome: `${(subject || "lettera").replace(/[^\p{L}\p{N}\-_]+/gu, "_")}.pdf`, mime: "application/pdf", content_b64: pdfB64 }]
        : [];
      const endpoint = account === "pec" ? "/studio/pec/send" : "/studio/email/send";
      setPhase("upload");
      await nextFrame();
      await api.post(endpoint, {
        pratica_id: pratica?.id, to: recipients, cc: finalCc.map((c) => c.email), bcc: finalCcn.map((c) => c.email),
        subject: subj, html: htmlBody, attachments, attachment_ids,
        soggetto: assistitoName || "", nome: letterName || subj, letter_id: letterId || null,
        cliente_id: clienteId || null, origine: "gestione_lettere",
        destinatari_info: [...finalTo, ...finalCc].map((r) => {
          const hit = (data.rubrica || []).flatMap((cat) => cat.contacts).find((c) => c.email.toLowerCase() === r.email.toLowerCase());
          const nome = (r.name && r.name !== r.email ? r.name : "") || (hit && hit.name !== "—" ? hit.name : "");
          return { email: r.email, nome, categoria: (hit?.role || "").replace(/\s*·\s*PEC$/, "") };
        }),
      }, {
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadPct(pct);
          if (pct >= 100) setPhase("server"); // upload finito: ora il server prepara allegati + SMTP
        },
      });
      setPhase("done");
      setUploadPct(100);
      emitSendStatus({ state: "ok", label: account === "pec" ? "PEC inviata con successo" : "Email inviata con successo" });
      toast.success(account === "pec" ? "PEC inviata con successo" : "Email inviata con successo");
      if (onSent) onSent();
      setEmailPreviewOpen(false);
      onOpenChange(false);
    } catch (e) {
      emitSendStatus({ state: "error", label: formatApiError(e.response?.data?.detail) || "Invio non riuscito" });
      toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito");
    } finally { setSending(false); setPhase(null); setUploadPct(0); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SuggeritiDestinatariDialog open={suggOpen} onClose={() => setSuggOpen(false)} praticaId={pratica?.id} mode={account === "pec" ? "pec" : "email"} onConfirm={addFromSuggeriti} />
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="send-letter-email-dialog"
        onPointerDownOutside={(e) => { if (previewIndex >= 0) e.preventDefault(); }}
        onInteractOutside={(e) => { if (previewIndex >= 0) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (previewIndex >= 0) { e.preventDefault(); setPreviewIndex(-1); } }}>
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-400" /> {account === "pec" ? "Invia via PEC" : "Invia via Email"} · {pratica?.numero_pratica}
          </DialogTitle>
        </DialogHeader>

        {curStatus && !canSend && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300 flex items-start gap-2" data-testid="sle-not-configured">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{account === "pec" ? "PEC" : "Email"} non configurata o disattivata. Il Titolare deve impostarla in <strong>Impostazioni Studio → {account === "pec" ? "Configurazione PEC" : "Configurazione Email"}</strong>.</span>
          </div>
        )}

        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : (
          <div className="space-y-4 pt-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Invia tramite</Label>
                <Select value={account} onValueChange={setAccount}>
                  <SelectTrigger data-testid="sle-account" className="mt-1.5 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">
                    <SelectItem value="email" data-testid="sle-account-email">Email (SMTP studio)</SelectItem>
                    <SelectItem value="pec" data-testid="sle-account-pec">PEC (SMTP PEC studio)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Mittente</Label>
                <Input value={curFrom || "—"} readOnly data-testid="sle-from" className="mt-1.5 bg-slate-900/40 border-slate-700 text-slate-400" />
              </div>
            </div>
            <RecipientField label="Destinatari" hint="(A)" value={to} onChange={setTo} rubrica={data.rubrica}
              onOpenRubrica={() => setRubricaField("to")} testId="sle-to" flushRef={toFlushRef} onSaveContact={saveContact} />
            <div className="grid sm:grid-cols-2 gap-4">
              <RecipientField label="CC" value={cc} onChange={setCc} rubrica={data.rubrica}
                onOpenRubrica={() => setRubricaField("cc")} testId="sle-cc" flushRef={ccFlushRef} onSaveContact={saveContact} />
              <RecipientField label="CCN" value={ccn} onChange={setCcn} rubrica={data.rubrica}
                onOpenRubrica={() => setRubricaField("ccn")} testId="sle-ccn" flushRef={ccnFlushRef} onSaveContact={saveContact} />
            </div>
            <div>
              <Label className="text-slate-300">Oggetto</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="sle-subject" className="mt-1.5 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-slate-300">Corpo del messaggio</Label>
                <div className="flex items-center gap-3">
                  {letterHtml ? (
                    <button type="button" onClick={toggleLetter} data-testid="sle-insert-letter"
                      className={`inline-flex items-center gap-1.5 text-xs rounded-md border px-2.5 py-1.5 transition-colors ${letterInserted ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"}`}>
                      <FileText className="h-3.5 w-3.5" /> {letterInserted ? "Rimuovi testo lettera" : "Inserisci testo lettera"}
                    </button>
                  ) : null}
                  <TestiRapidiPicker onInsert={insertQuickText} testId="sle-testi-rapidi" />
                  <div className="flex items-center gap-2">
                    <Switch checked={includeSig} onCheckedChange={changeIncludeSig} data-testid="sle-include-sig" className="data-[state=checked]:bg-amber-500" />
                    <Label className="text-slate-300 text-sm cursor-pointer">Includi firma</Label>
                  </div>
                </div>
              </div>
              <style>{`${LETTER_CSS}\n.sle-body-editor:empty:before{content:attr(data-placeholder);color:#94a3b8}\n.sle-body-editor .sle-letter-embed{margin-top:8px}`}</style>
              {emailHeaderHtml(emailHead) ? (
                <div className="bg-white border border-b-0 border-slate-300 rounded-t-md px-4 pt-3 -mb-1" contentEditable={false} title="Intestazione dello studio (inviata in cima all'email)"
                  data-testid="sle-body-header" dangerouslySetInnerHTML={{ __html: emailHeaderHtml(emailHead) }} />
              ) : null}
              <div ref={bodyRef} contentEditable suppressContentEditableWarning
                onInput={(e) => setBody(e.currentTarget.innerHTML)}
                data-testid="sle-body" data-placeholder="Scrivi il messaggio…"
                className={`sle-body-editor bg-white text-slate-900 border border-slate-300 ${emailHeaderHtml(emailHead) ? "rounded-b-md border-t-0" : "rounded-md"} px-4 py-3 min-h-[180px] max-h-[360px] overflow-y-auto overflow-x-hidden text-[15px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/40`} />
              {includeSig && data.signature && (
                <div className="mt-2 rounded bg-slate-900/40 border border-slate-800 px-3 py-2 text-xs text-slate-400 whitespace-pre-wrap font-mono" data-testid="sle-signature-preview">{`--\n${data.signature}`}</div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-slate-300">Allegati</Label>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => { flushRecipients(); setEmailPreviewOpen(true); }} disabled={!canSend} data-testid="sle-preview-button-top"
                    className="h-8 border-amber-500 text-amber-400 hover:bg-amber-500/10 bg-transparent">
                    <Eye className="h-4 w-4 mr-1" /> Anteprima Email
                  </Button>
                  <Button size="sm" onClick={send} disabled={sending || !canSend || overLimit} data-testid="sle-send-button-top" className="h-8 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Invia
                  </Button>
                {data.attachments.length > 0 && (
                  <div className="flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-900/60 p-0.5" data-testid="sle-att-view-toggle">
                    <button type="button" onClick={() => changeView("list")} title="Vista lista" data-testid="sle-att-view-list"
                      className={`h-7 w-7 flex items-center justify-center rounded-md transition-colors ${attView === "list" ? "bg-amber-500 text-[#0a0d14]" : "text-slate-400 hover:text-slate-100"}`}>
                      <List className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => changeView("grid")} title="Vista miniature" data-testid="sle-att-view-grid"
                      className={`h-7 w-7 flex items-center justify-center rounded-md transition-colors ${attView === "grid" ? "bg-amber-500 text-[#0a0d14]" : "text-slate-400 hover:text-slate-100"}`}>
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                  </div>
                )}
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-200 bg-slate-900/50 rounded px-3 py-2 border border-slate-700" data-testid="sle-letter-attachment">
                <Checkbox checked disabled />
                <Paperclip className="h-4 w-4 text-amber-400" />
                <span className="truncate">{letterName || "Lettera corrente"} (PDF) — allegata automaticamente</span>
              </div>

              <div className="mt-2 flex items-center justify-between text-[11px]" data-testid="sle-att-total">
                <span className="text-slate-400">Totale allegati selezionati: <span className={`font-mono ${overLimit ? "text-red-300" : "text-slate-200"}`}>{fmtBytes(selectedTotal)}</span> <span className="text-slate-500">/ {fmtBytes(maxAttachBytes)} max (+ PDF lettera)</span></span>
              </div>
              {overLimit && (
                <div className="mt-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300 flex items-start gap-2" data-testid="sle-att-over-limit">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Gli allegati selezionati ({fmtBytes(selectedTotal)}) superano il limite di {fmtBytes(maxAttachBytes)}. Deseleziona qualche file prima di inviare.</span>
                </div>
              )}

              {attView === "list" && data.attachments.length > 0 && (
                <p className="mt-2 text-[11px] text-slate-500">Doppio click su un allegato per l'anteprima a schermo intero.</p>
              )}
              {attView === "grid" && (
                <p className="mt-2 text-[11px] text-slate-500">Doppio click su un allegato per l'anteprima a schermo intero.</p>
              )}

              {attSections.map((sec) => {
                const secFiles = sec.cats.flatMap((c) => c.files);
                const secIds = secFiles.map((f) => f.id);
                const nSel = secFiles.filter((f) => selectedIds[f.id]).length;
                const all = secFiles.length > 0 && nSel === secFiles.length;
                const extraSec = sec.key !== "principale";
                const tp = extraSec ? `-${sec.key}` : "";
                const fOpen = !!fotoOpen[sec.key];
                const secOpen = !extraSec || !!openSecs[sec.key];
                const secClick = (id) => (e) => attClick(id, secIds)(e);
                return (
                <div key={sec.key} data-testid={`sle-att-section-${extraSec ? sec.key : "principale"}`}
                  className={`mt-3 rounded-lg border p-2.5 ${extraSec ? "border-sky-500/50 bg-sky-500/[0.05]" : "border-amber-500/40 bg-amber-500/[0.03]"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {extraSec && (
                      <button type="button" onClick={() => setOpenSecs((o) => ({ ...o, [sec.key]: !o[sec.key] }))} data-testid={`sle-att-section-toggle${tp}`} aria-expanded={secOpen}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-sky-500/50 text-[11px] font-semibold text-sky-300 hover:bg-sky-500/10">
                        {secOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {secOpen ? "Chiudi" : "Apri"}
                      </button>
                    )}
                    <div className={`text-xs font-bold uppercase tracking-wider ${extraSec ? "text-sky-400" : "text-amber-400"}`} data-testid={`sle-att-section-title${tp}`}>Allegati — {sec.nome}</div>
                  </div>
                  {extraSec && <p className="text-[11px] text-slate-500">Nessun file di questo cliente è selezionato: scegli tu quali includere.</p>}
                  <div className="mt-2 flex items-center gap-3 flex-wrap" data-testid={`sle-att-selbar${tp}`}>
                    <button type="button" onClick={() => setSelectedIds((s) => ({ ...s, ...Object.fromEntries(secIds.map((x) => [x, !all])) }))} data-testid={`sle-att-select-all${tp}`} className="h-8 px-3 rounded-md border border-slate-600 text-xs font-semibold text-slate-200 hover:bg-slate-800 inline-flex items-center gap-2">
                      <Checkbox checked={all} className="pointer-events-none" tabIndex={-1} /> {all ? "Deseleziona tutto" : "Seleziona tutto"}
                    </button>
                    <span className="text-xs text-slate-300" data-testid={`sle-att-selcount${tp}`}>{nSel} {nSel === 1 ? "selezionato" : "selezionati"}</span>
                    {!extraSec && <span className="hidden sm:inline text-[11px] text-slate-500">Ctrl+clic (Cmd su Mac) per scegliere più file, Maiusc+clic per un intervallo.</span>}
                  </div>
              {secOpen && sec.cats.map((cat) => {
                const isFoto = /^foto/i.test(cat.categoria || "");
                return (
                <div key={cat.categoria} className="mt-3">
                  {isFoto ? (
                    <button type="button" onClick={() => setFotoOpen((o) => ({ ...o, [sec.key]: !o[sec.key] }))} data-testid={`sle-foto-toggle${tp}`} aria-expanded={fOpen}
                      className="flex items-center gap-1 text-[11px] uppercase tracking-wider font-mono text-slate-500 hover:text-amber-400 mb-1 transition-colors">
                      {fOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{cat.categoria}
                      <span className="normal-case tracking-normal">({cat.files.length}{cat.files.some((f) => selectedIds[f.id]) ? `, ${cat.files.filter((f) => selectedIds[f.id]).length} selezionate` : ""})</span>
                    </button>
                  ) : (
                    <div className="text-[11px] uppercase tracking-wider font-mono text-slate-500 mb-1">{cat.categoria}</div>
                  )}

                  {isFoto && !fOpen ? null : attView === "list" ? (
                    <div className="space-y-1">
                      {cat.files.map((f) => (
                        <label key={f.id} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-900/40 rounded px-3 py-1.5 cursor-pointer select-none hover:bg-slate-800/50" data-testid={`sle-att-${f.id}`}
                          onClickCapture={secClick(f.id)} onMouseDown={noShiftTextSelect}
                          onDoubleClick={() => setPreviewIndex(flatFiles.findIndex((x) => x.id === f.id))}>
                          <Checkbox checked={!!selectedIds[f.id]} onCheckedChange={() => toggleAtt(f.id)} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{f.nome}</div>
                            <AttFolderLine f={f} className="text-xs" />
                          </div>
                          <span className="text-xs text-slate-500 font-mono shrink-0">{fmtBytes(f.size)}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {cat.files.map((f) => (
                        <label key={f.id}
                          className={`group relative flex flex-col rounded-lg border overflow-hidden cursor-pointer select-none transition-colors ${selectedIds[f.id] ? "border-amber-500 bg-amber-500/10" : "border-slate-700 bg-slate-900/40 hover:border-slate-500"}`}
                          data-testid={`sle-att-${f.id}`} onClickCapture={secClick(f.id)} onMouseDown={noShiftTextSelect}
                          onDoubleClick={() => setPreviewIndex(flatFiles.findIndex((x) => x.id === f.id))}>
                          <div className="absolute top-1.5 left-1.5 z-10 rounded bg-slate-900/80 p-0.5">
                            <Checkbox checked={!!selectedIds[f.id]} onCheckedChange={() => toggleAtt(f.id)} data-testid={`sle-att-check-${f.id}`} />
                          </div>
                          <div className="aspect-square flex items-center justify-center bg-slate-950/40 overflow-hidden">
                            <AttachmentThumb pid={pratica?.id} file={f} />
                          </div>
                          <div className="px-1.5 py-1">
                            <div className="text-[11px] text-slate-300 truncate" title={f.nome}>{f.nome}</div>
                            <AttFolderLine f={f} className="text-[11px]" />
                            <div className="text-[10px] text-slate-500 font-mono">{fmtBytes(f.size)}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
                </div>
                );
              })}
            </div>

            {sending && phaseLabel && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="sle-progress">
                <div className="flex items-center justify-between text-xs text-amber-200 mb-1.5">
                  <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {phaseLabel}</span>
                  <span className="font-mono" data-testid="sle-progress-pct">{progressPct}%</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full bg-amber-500 transition-all duration-300" style={{ width: `${progressPct}%` }} data-testid="sle-progress-bar" />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => { flushRecipients(); setEmailPreviewOpen(true); }} disabled={!canSend} data-testid="sle-preview-button"
                className="border-amber-500 text-amber-400 hover:bg-amber-500/10 bg-transparent">
                <Eye className="h-4 w-4 mr-1" /> Anteprima Email
              </Button>
              <Button onClick={send} disabled={sending || !canSend || overLimit} data-testid="sle-send-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Invia
              </Button>
            </div>
          </div>
        )}
        {emailPreviewOpen && (
          <EmailPreviewModal
            open={emailPreviewOpen}
            onClose={() => setEmailPreviewOpen(false)}
            onSend={send}
            sending={sending}
            from={curFrom}
            to={to}
            cc={cc}
            ccn={ccn}
            subject={subject}
            body={body}
            bodyIsHtml={true}
            letterHtml={null}
            headerHtml={emailHeaderHtml(emailHead)}
            signature={includeSig ? data.signature : null}
            attachments={previewAttachments}
            phaseLabel={phaseLabel}
            progressPct={progressPct}
          />
        )}
      </DialogContent>

      <RubricaDialog
        open={!!rubricaField}
        onClose={() => setRubricaField(null)}
        categories={data.rubrica}
        fieldLabel={rubricaField ? FIELD[rubricaField].label : ""}
        currentEmails={new Set((rubricaField ? FIELD[rubricaField].value : []).map((c) => c.email.toLowerCase()))}
        onAdd={(list) => rubricaField && mergeInto(rubricaField, list)}
      />

      {previewIndex >= 0 && flatFiles[previewIndex] && (
        <AttachmentPreviewModal
          files={flatFiles}
          index={previewIndex}
          onIndex={setPreviewIndex}
          pid={pratica?.id}
          onClose={() => setPreviewIndex(-1)}
        />
      )}
    </Dialog>
  );
}
